import {
  BondStatus,
  BondType,
  CountryDomicile,
} from '@moment/trading-types/reference-data'
import { capitalize } from 'lodash'

import {
  formatDecimal,
  formatNumber,
  formatPercentage,
  isNullish,
  PLACEHOLDER,
} from '@/utils/presentation/formatting'

/**
 * Default par values for different bond types.
 * Used to set initial quantities in order tickets and forms.
 */
export const DEFAULT_PAR_VALUE = {
  MUNICIPAL: 5000,
  STANDARD: 1000,
} as const

/**
 * Multiplier used to round out floating-point noise after division.
 * 1e6 preserves up to 6 decimal places — well beyond bond increment
 * precision (typically 0.01) while eliminating artifacts like
 * 1248.16 / 1000 → 1.2481600000000002.
 */
export const QUANTITY_PRECISION_FACTOR = 1e6

/**
 * The tradeable increment, expressed in the $1,000 units the UI displays: a
 * $5,000-denomination muni steps 5 at a time. Sub-$1,000 denominations would
 * step fractionally, which no cell can render — they step by one bond.
 */
export function getQuantityStep(parValuePerBond?: number | null): number {
  if (
    parValuePerBond == null ||
    parValuePerBond <= DEFAULT_PAR_VALUE.STANDARD
  ) {
    return 1
  }
  return parValuePerBond / DEFAULT_PAR_VALUE.STANDARD
}

/**
 * Converts a number of bonds to par value.
 * For example, 5 bonds at $1000 par value = $5000 par value.
 *
 * @param numBonds - The number of bonds to convert
 * @returns The par value amount
 */
export function convertNumBondsToParValue(numBonds: number): number {
  const result = numBonds * DEFAULT_PAR_VALUE.STANDARD
  return (
    Math.round(result * QUANTITY_PRECISION_FACTOR) / QUANTITY_PRECISION_FACTOR
  )
}

/**
 * Converts a par value to number of bonds.
 * For example, $5000 par value at $1000 par value per bond = 5 bonds.
 *
 * @param parValue - The par value amount to convert
 * @returns The number of bonds
 */
export function convertParValueToNumBonds(parValue: number): number {
  const result = parValue / DEFAULT_PAR_VALUE.STANDARD
  return (
    Math.round(result * QUANTITY_PRECISION_FACTOR) / QUANTITY_PRECISION_FACTOR
  )
}

/**
 * Formats a quantity value as a whole number with comma separators.
 *
 * @param value - The numeric quantity value to format
 * @param options - Options for formatting:
 *   @param options.useZeroPlaceholder - Whether to show a placeholder for zero values (defaults to false)
 *   @param options.isAlreadyInNumBonds - Whether the input value is already in number of bonds (true) or par value (false) (defaults to false)
 * @returns A formatted string with comma separators (e.g. "1,000"),
 *          no decimal places, and no currency symbol.
 *          Returns a placeholder if the value is nullish.
 */
interface FormatQuantityOptions {
  useZeroPlaceholder?: boolean
  isAlreadyInNumBonds?: boolean
}

export function formatQuantity(
  value: number | null | undefined,
  options: FormatQuantityOptions = {},
): string {
  if (isNullish(value)) return PLACEHOLDER
  const { useZeroPlaceholder = false, isAlreadyInNumBonds = false } = options
  const displayValue = isAlreadyInNumBonds
    ? value
    : convertParValueToNumBonds(value as number)
  return formatNumber(displayValue as number, {
    maximumFractionDigits: 3, // Since quantity can be in increments of $1 for fractional bonds
    useZeroPlaceholder,
  })
}

/**
 * Formats a yield value as a percentage with 3 decimal places.
 *
 * @param value - The yield value to format, which can be a number or null
 * @returns A formatted percentage string with 3 decimal places (e.g. "5.123%").
 *          Returns a placeholder if the value is nullish.
 */
export function formatYield(
  value: number | null | undefined,
  useZeroPlaceholder = false,
  digits = 3,
): string {
  if (isNullish(value)) return PLACEHOLDER
  return formatPercentage(value as number, {
    numDigits: digits,
    useZeroPlaceholder,
  })
}

/**
 * Formats a basis points (BPS) value with specified decimal places.
 * Used for displaying spread values in order books and trade tables.
 *
 * @param value - The basis points value to format, which can be a number or null
 * @param decimalPlaces - Number of decimal places to display (defaults to 2)
 * @returns A formatted string with the specified decimal places.
 *          Returns a placeholder if the value is nullish.
 */
export function formatBps(
  value: number | null | undefined,
  decimalPlaces: number = 2,
): string {
  if (isNullish(value)) {
    return PLACEHOLDER
  }
  return formatDecimal(value as number, decimalPlaces)
}

/**
 * Formats a basis points (BPS) value with at most `maxDecimals` decimal
 * places, dropping trailing zeros (`2.5`, `26.161`, `38`).
 */
export function formatBpsUpTo(
  value: number | null | undefined,
  maxDecimals: number,
): string {
  if (isNullish(value)) {
    return PLACEHOLDER
  }
  return String(Number((value as number).toFixed(maxDecimals)))
}

/**
 * Canonical mapping of BondType enum values to display text.
 * This is the single source of truth for how bond types should be displayed across the platform.
 *
 * Display labels:
 * - Government displays as "Sovereign" (changed in https://linear.app/mmnt/issue/QT-1084/cleanups)
 * - MoneyMarket displays as "MMF" (Money Market Fund)
 * - All other bond types use their standard capitalized names
 */
export const BOND_TYPE_DISPLAY_MAP: Record<BondType, string> = {
  [BondType.Agency]: 'Agency',
  [BondType.CD]: 'CD',
  [BondType.Corporate]: 'Corporate',
  [BondType.Government]: 'Sovereign',
  [BondType.MoneyMarket]: 'MMF',
  [BondType.Municipal]: 'Municipal',
  [BondType.Securitized]: 'Securitized',
  [BondType.Treasury]: 'Treasury',
}

// Fund instrument classes surfaced by the all-asset histogram buckets; these
// arrive through the same `type` field but aren't BondType members, and
// capitalize() would render 'Exchangetradedfund'.
const FUND_CLASS_DISPLAY_MAP: Record<string, string> = {
  Fund: 'MF',
  ExchangeTradedFund: 'ETF',
  ExchangeTradedCommodity: 'ETC',
}

/**
 * Formats a bond type as a human-readable string for presentation.
 *
 * @param bondType - The bond type to format.
 */
export function formatBondType(bondType: BondType): string {
  return (
    BOND_TYPE_DISPLAY_MAP[bondType] ||
    FUND_CLASS_DISPLAY_MAP[bondType] ||
    capitalize(bondType)
  )
}

/**
 * Formats a bond status as a human-readable string for presentation.
 * Special-cases PreIssuance as "New issue", all others are capitalized.
 *
 * @param bondStatus - The bond status to format.
 * @example formatBondStatus(BondStatus.PreIssuance) // "New issue"
 * @example formatBondStatus(BondStatus.Outstanding) // "Outstanding"
 */
export function formatBondStatus(bondStatus: BondStatus): string {
  if (bondStatus === BondStatus.PreIssuance) {
    return 'New issue'
  }
  return capitalize(bondStatus)
}

/**
 * Display names for country ISO codes.
 *
 * Maps {@link CountryDomicile} enum values to human-readable country names.
 * Can be used for country_domicile, country_issue, and country_of_risk fields
 * as all are subsets of or identical to CountryDomicile values.
 *
 * @example
 * ```tsx
 * const bond = { country_domicile: CountryDomicile.US }
 * const displayName = COUNTRY_LABELS[bond.country_domicile]  // 'United States'
 *
 * // Also works for country_issue and country_of_risk
 * const countryOfRisk = 'CA'
 * const riskCountryName = COUNTRY_LABELS[countryOfRisk]  // 'Canada'
 * ```
 *
 * Note: This mapping is temporary until we have the backend APIs for reference data fields.
 */
export const COUNTRY_LABELS: Record<keyof typeof CountryDomicile, string> = {
  [CountryDomicile.AU]: 'Australia',
  [CountryDomicile.NL]: 'Netherlands',
  [CountryDomicile.DE]: 'Germany',
  [CountryDomicile.FR]: 'France',
  [CountryDomicile.CH]: 'Switzerland',
  [CountryDomicile.IT]: 'Italy',
  [CountryDomicile.JP]: 'Japan',
  [CountryDomicile.AT]: 'Austria',
  [CountryDomicile.BE]: 'Belgium',
  [CountryDomicile.DK]: 'Denmark',
  [CountryDomicile.ES]: 'Spain',
  [CountryDomicile.GR]: 'Greece',
  [CountryDomicile.IE]: 'Ireland',
  [CountryDomicile.LU]: 'Luxembourg',
  [CountryDomicile.NO]: 'Norway',
  [CountryDomicile.PT]: 'Portugal',
  [CountryDomicile.SE]: 'Sweden',
  [CountryDomicile.AR]: 'Argentina',
  [CountryDomicile.BR]: 'Brazil',
  [CountryDomicile.CL]: 'Chile',
  [CountryDomicile.MX]: 'Mexico',
  [CountryDomicile.PE]: 'Peru',
  [CountryDomicile.VE]: 'Venezuela, Bolivarian Republic of',
  [CountryDomicile.NZ]: 'New Zealand',
  [CountryDomicile.HK]: 'Hong Kong',
  [CountryDomicile.SG]: 'Singapore',
  [CountryDomicile.TH]: 'Thailand',
  [CountryDomicile.ID]: 'Indonesia',
  [CountryDomicile.TW]: 'Taiwan',
  [CountryDomicile.KR]: 'Korea, Republic of',
  [CountryDomicile.PH]: 'Philippines',
  [CountryDomicile.CZ]: 'Czech Republic',
  [CountryDomicile.PL]: 'Poland',
  [CountryDomicile.HU]: 'Hungary',
  [CountryDomicile.RU]: 'Russian Federation',
  [CountryDomicile.TR]: 'Turkey',
  [CountryDomicile.CN]: 'China',
  [CountryDomicile.IN]: 'India',
  [CountryDomicile.PK]: 'Pakistan',
  [CountryDomicile.LK]: 'Sri Lanka',
  [CountryDomicile.IL]: 'Israel',
  [CountryDomicile.EG]: 'Egypt',
  [CountryDomicile.MA]: 'Morocco',
  [CountryDomicile.ZA]: 'South Africa',
  [CountryDomicile.JM]: 'Jamaica',
  [CountryDomicile.CO]: 'Colombia',
  [CountryDomicile.GB]: 'United Kingdom',
  [CountryDomicile.FI]: 'Finland',
  [CountryDomicile.EC]: 'Ecuador',
  [CountryDomicile.MY]: 'Malaysia',
  [CountryDomicile.BH]: 'Bahrain',
  [CountryDomicile.SK]: 'Slovakia',
  [CountryDomicile.JO]: 'Jordan',
  [CountryDomicile.EE]: 'Estonia',
  [CountryDomicile.CY]: 'Cyprus',
  [CountryDomicile.LT]: 'Lithuania',
  [CountryDomicile.LV]: 'Latvia',
  [CountryDomicile.MT]: 'Malta',
  [CountryDomicile.HR]: 'Croatia',
  [CountryDomicile.GH]: 'Ghana',
  [CountryDomicile.TN]: 'Tunisia',
  [CountryDomicile.NG]: 'Nigeria',
  [CountryDomicile.LB]: 'Lebanon',
  [CountryDomicile.QA]: 'Qatar',
  [CountryDomicile.OM]: 'Oman',
  [CountryDomicile.BD]: 'Bangladesh',
  [CountryDomicile.KZ]: 'Kazakhstan',
  [CountryDomicile.TT]: 'Trinidad and Tobago',
  [CountryDomicile.UA]: 'Ukraine',
  [CountryDomicile.KW]: 'Kuwait',
  [CountryDomicile.BG]: 'Bulgaria',
  [CountryDomicile.BM]: 'Bermuda',
  [CountryDomicile.AE]: 'United Arab Emirates',
  [CountryDomicile.VN]: 'Viet Nam',
  [CountryDomicile.AF]: 'Afghanistan',
  [CountryDomicile.AX]: 'Aland Islands',
  [CountryDomicile.AL]: 'Albania',
  [CountryDomicile.DZ]: 'Algeria',
  [CountryDomicile.AS]: 'American Samoa',
  [CountryDomicile.AD]: 'Andorra',
  [CountryDomicile.AO]: 'Angola',
  [CountryDomicile.AI]: 'Anguilla',
  [CountryDomicile.AQ]: 'Antarctica',
  [CountryDomicile.AG]: 'Antigua and Barbuda',
  [CountryDomicile.AM]: 'Armenia',
  [CountryDomicile.AW]: 'Aruba',
  [CountryDomicile.AZ]: 'Azerbaijan',
  [CountryDomicile.BS]: 'Bahamas',
  [CountryDomicile.BB]: 'Barbados',
  [CountryDomicile.BY]: 'Belarus',
  [CountryDomicile.BZ]: 'Belize',
  [CountryDomicile.BJ]: 'Benin',
  [CountryDomicile.BT]: 'Bhutan',
  [CountryDomicile.BO]: 'Bolivia, Plurinational State of',
  [CountryDomicile.BQ]: 'Bonaire, Sint Eustatius and Saba',
  [CountryDomicile.BA]: 'Bosnia and Herzegovina',
  [CountryDomicile.BW]: 'Botswana',
  [CountryDomicile.BV]: 'Bouvet Island',
  [CountryDomicile.IO]: 'British Indian Ocean Territory',
  [CountryDomicile.BN]: 'Brunei Darussalam',
  [CountryDomicile.BF]: 'Burkina Faso',
  [CountryDomicile.BI]: 'Burundi',
  [CountryDomicile.KH]: 'Cambodia',
  [CountryDomicile.CM]: 'Cameroon',
  [CountryDomicile.CA]: 'Canada',
  [CountryDomicile.CV]: 'Cape Verde',
  [CountryDomicile.KY]: 'Cayman Islands',
  [CountryDomicile.CF]: 'Central African Republic',
  [CountryDomicile.TD]: 'Chad',
  [CountryDomicile.CX]: 'Christmas Island',
  [CountryDomicile.CC]: 'Cocos (Keeling) Islands',
  [CountryDomicile.CD]: 'Congo, the Democratic Republic of the',
  [CountryDomicile.CG]: 'Congo',
  [CountryDomicile.CK]: 'Cook Islands',
  [CountryDomicile.CR]: 'Costa Rica',
  [CountryDomicile.CI]: "Cote d'Ivoire",
  [CountryDomicile.CU]: 'Cuba',
  [CountryDomicile.CW]: 'Curacao',
  [CountryDomicile.DJ]: 'Djibouti',
  [CountryDomicile.DM]: 'Dominica',
  [CountryDomicile.DO]: 'Dominican Republic',
  [CountryDomicile.SV]: 'El Salvador',
  [CountryDomicile.GQ]: 'Equatorial Guinea',
  [CountryDomicile.ER]: 'Eritrea',
  [CountryDomicile.ET]: 'Ethiopia',
  [CountryDomicile.FK]: 'Falkland Islands (Malvinas)',
  [CountryDomicile.FO]: 'Faroe Islands',
  [CountryDomicile.FJ]: 'Fiji',
  [CountryDomicile.PF]: 'French Polynesia',
  [CountryDomicile.TF]: 'French Southern Territories',
  [CountryDomicile.GA]: 'Gabon',
  [CountryDomicile.GM]: 'Gambia',
  [CountryDomicile.GE]: 'Georgia',
  [CountryDomicile.GI]: 'Gibraltar',
  [CountryDomicile.GL]: 'Greenland',
  [CountryDomicile.GD]: 'Grenada',
  [CountryDomicile.GP]: 'Guadeloupe',
  [CountryDomicile.GU]: 'Guam',
  [CountryDomicile.GT]: 'Guatemala',
  [CountryDomicile.GG]: 'Guernsey',
  [CountryDomicile.GN]: 'Guinea',
  [CountryDomicile.GW]: 'Guinea-Bissau',
  [CountryDomicile.GY]: 'Guyana',
  [CountryDomicile.HT]: 'Haiti',
  [CountryDomicile.HM]: 'Heard Island and McDonald Islands',
  [CountryDomicile.VA]: 'Holy See (Vatican City State)',
  [CountryDomicile.HN]: 'Honduras',
  [CountryDomicile.IS]: 'Iceland',
  [CountryDomicile.IR]: 'Iran, Islamic Republic of',
  [CountryDomicile.IQ]: 'Iraq',
  [CountryDomicile.IM]: 'Isle of Man',
  [CountryDomicile.JE]: 'Jersey',
  [CountryDomicile.KE]: 'Kenya',
  [CountryDomicile.KI]: 'Kiribati',
  [CountryDomicile.KP]: "Korea, Democratic People's Republic of",
  [CountryDomicile.KG]: 'Kyrgyzstan',
  [CountryDomicile.LA]: "Lao People's Democratic Republic",
  [CountryDomicile.LS]: 'Lesotho',
  [CountryDomicile.LR]: 'Liberia',
  [CountryDomicile.LY]: 'Libyan Arab Jamahiriya',
  [CountryDomicile.LI]: 'Liechtenstein',
  [CountryDomicile.MO]: 'Macao',
  [CountryDomicile.MK]: 'Macedonia, the former Yugoslav Republic of',
  [CountryDomicile.MG]: 'Madagascar',
  [CountryDomicile.MW]: 'Malawi',
  [CountryDomicile.MV]: 'Maldives',
  [CountryDomicile.ML]: 'Mali',
  [CountryDomicile.MH]: 'Marshall Islands',
  [CountryDomicile.MQ]: 'Martinique',
  [CountryDomicile.MR]: 'Mauritania',
  [CountryDomicile.MU]: 'Mauritius',
  [CountryDomicile.YT]: 'Mayotte',
  [CountryDomicile.FM]: 'Micronesia, Federated States of',
  [CountryDomicile.MD]: 'Moldova, Republic of',
  [CountryDomicile.MC]: 'Monaco',
  [CountryDomicile.MN]: 'Mongolia',
  [CountryDomicile.ME]: 'Montenegro',
  [CountryDomicile.MS]: 'Montserrat',
  [CountryDomicile.MZ]: 'Mozambique',
  [CountryDomicile.MM]: 'Myanmar',
  [CountryDomicile.NA]: 'Namibia',
  [CountryDomicile.NR]: 'Nauru',
  [CountryDomicile.NP]: 'Nepal',
  [CountryDomicile.AN]: 'Netherlands Antilles',
  [CountryDomicile.NC]: 'New Caledonia',
  [CountryDomicile.NI]: 'Nicaragua',
  [CountryDomicile.NE]: 'Niger',
  [CountryDomicile.NU]: 'Niue',
  [CountryDomicile.NF]: 'Norfolk Island',
  [CountryDomicile.MP]: 'Northern Mariana Islands',
  [CountryDomicile.PW]: 'Palau',
  [CountryDomicile.PS]: 'Palestinian Territory, Occupied',
  [CountryDomicile.PA]: 'Panama',
  [CountryDomicile.PG]: 'Papua New Guinea, Independent State of',
  [CountryDomicile.PY]: 'Paraguay',
  [CountryDomicile.PN]: 'Pitcairn',
  [CountryDomicile.PR]: 'Puerto Rico',
  [CountryDomicile.RE]: 'Reunion',
  [CountryDomicile.RO]: 'Romania',
  [CountryDomicile.RW]: 'Rwanda',
  [CountryDomicile.BL]: 'Saint Barthelemy',
  [CountryDomicile.SH]: 'Saint Helena, Ascension and Tristan da Cunha',
  [CountryDomicile.KN]: 'Saint Kitts and Nevis',
  [CountryDomicile.LC]: 'Saint Lucia',
  [CountryDomicile.MF]: 'Saint Martin',
  [CountryDomicile.SX]: 'Sint Maarten',
  [CountryDomicile.PM]: 'Saint Pierre and Miquelon',
  [CountryDomicile.VC]: 'Saint Vincent and the Grenadines',
  [CountryDomicile.WS]: 'Samoa',
  [CountryDomicile.SM]: 'San Marino',
  [CountryDomicile.ST]: 'Sao Tome and Principe',
  [CountryDomicile.SA]: 'Saudi Arabia',
  [CountryDomicile.SN]: 'Senegal',
  [CountryDomicile.RS]: 'Serbia',
  [CountryDomicile.SC]: 'Seychelles',
  [CountryDomicile.SL]: 'Sierra Leone',
  [CountryDomicile.SI]: 'Slovenia',
  [CountryDomicile.SB]: 'Solomon Islands',
  [CountryDomicile.SO]: 'Somalia, Federal Republic of',
  [CountryDomicile.GS]: 'South Georgia and the South Sandwich Islands',
  [CountryDomicile.SS]: 'South Sudan',
  [CountryDomicile.SD]: 'Sudan',
  [CountryDomicile.SR]: 'Suriname',
  [CountryDomicile.SJ]: 'Svalbard and Jan Mayen',
  [CountryDomicile.SZ]: 'Swaziland',
  [CountryDomicile.SY]: 'Syrian Arab Republic',
  [CountryDomicile.TJ]: 'Tajikistan',
  [CountryDomicile.GF]: 'French Guiana',
  [CountryDomicile.TZ]: 'Tanzania, United Republic of',
  [CountryDomicile.TL]: "Timor-L'Este",
  [CountryDomicile.TG]: 'Togo',
  [CountryDomicile.TK]: 'Tokelau',
  [CountryDomicile.TO]: 'Tonga',
  [CountryDomicile.TM]: 'Turkmenistan',
  [CountryDomicile.TC]: 'Turks and Caicos Island',
  [CountryDomicile.TV]: 'Tuvalu',
  [CountryDomicile.UG]: 'Uganda',
  [CountryDomicile.UM]: 'United States Minor Outlying Islands',
  [CountryDomicile.US]: 'United States',
  [CountryDomicile.UY]: 'Uruguay',
  [CountryDomicile.VU]: 'Vanuatu',
  [CountryDomicile.VG]: 'Virgin Islands, British',
  [CountryDomicile.VI]: 'Virgin Islands, U.S.',
  [CountryDomicile.WF]: 'Wallis and Futuna',
  [CountryDomicile.EH]: 'Western Sahara',
  [CountryDomicile.YE]: 'Yemen',
  [CountryDomicile.ZM]: 'Zambia',
  [CountryDomicile.ZW]: 'Zimbabwe',
  [CountryDomicile.AC]: 'Ascension Island',
  [CountryDomicile.CP]: 'Clipperton Island',
  [CountryDomicile.DG]: 'Diego Garcia',
  [CountryDomicile.TA]: 'Tristan da Cunha',
  [CountryDomicile.KM]: 'Comoros',
  [CountryDomicile.UZ]: 'Uzbekistan',
  [CountryDomicile.Z1]: 'Asia Pacific Zone',
  [CountryDomicile.Z2]: 'European Zone',
  [CountryDomicile.Z3]: 'Americas Zone',
  [CountryDomicile.Z4]: 'Africa Zone',
  [CountryDomicile.EU]: 'EU Member State',
  [CountryDomicile.XB]: 'Supranational',
  [CountryDomicile.XS]: 'EuroMarket',
  [CountryDomicile.Z5]: 'EUSD Jurisdiction not provided',
}
